import * as React from 'react';
import { useAppSelector, useAppSelector as useSelector } from 'Store/index';
import {
  calculateDaysDifference,
  formatTime,
  isNumber,
  isPartialDate,
  nowPartialDate,
  parsePatientDemographics,
  partialDateToValue,
} from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import FormRow from 'Components/FormRow';
import InputHandler from 'Components/InputHandler';
import { useMyServiceContext } from '../../index';
import { StyledTextArea } from 'Components/InputHandler/components/TextArea';
import colors from '../../../../../config/theme/colors';
import { isTaskActionType } from 'neuro-schemas';
import { DateTime } from 'luxon';
import StyledDateRange from '../components/StyledDateRange';
import LeftRightControls from '../components/LeftRightControls';
import { styled } from '@mui/system';
import phone from 'phone';
import { filterDocumentsByType, sortDocumentsDescending } from 'Utility/documentHandling';
import { validate as emailValidate } from '@stellarq/email';
import { formatTaskActionType, getTaskActionTypeOptions } from 'Routes/MyService/util';
import TextField from 'Components/InputHandler/components/TextField';
import { Container, Item } from 'Components/Grid';
import {
  getLatestContraIndicationsToTreatmentDoc,
  getTDCSAndRTMSDocs,
  parseRecurringTaskDeadline,
  parseSendDate,
  shouldAllowRecurringTasks,
} from './utils';
import Censorable from 'Components/Censorable';

import { NewInformant } from './NewInformant';
import {
  partialDateFromEndExclusive,
  partialDateFromStartInclusive,
  partialDateToEndExclusive,
  partialDateToStartInclusive,
} from 'Utility/tasklist';
import browserLanguage from 'Utility/browserLanguage';

const TASK_DATE_FORMAT = 'yyyy-MM-dd HH:mm:ss' as const;

const mapRecurringIntervalToEveryNth = (
  interval: TaskStimulusFormSettings[number]['recurringInterval'],
): number | undefined => {
  if (!interval) return undefined;
  const map: Record<NonNullable<TaskStimulusFormSettings[number]['recurringInterval']>, number> = {
    everyFourthWeek: 4,
    everyOtherWeek: 2,
    weekly: 1,
  };
  return map[interval];
};

const formateTemplateIDOption = ({
  option,
  templateSettings,
  locale,
  fm,
}: {
  option: string | number;
  templateSettings: TaskStimulusFormSettings | undefined;
  locale: Locale;
  fm: (id: string) => string;
}): string => {
  if (!templateSettings || !Object.keys(templateSettings).length) return fm(`myService.opts.${option}`);
  // oxlint-disable-next-line typescript/no-unnecessary-condition
  return templateSettings[option]?.titleLocalized[locale] ?? templateSettings[option]?.titleLocalized.fi ?? `${option}`;
};

const StyledButton = styled('a')({
  fontSize: '1.6rem',
  fontWeight: 600,
  cursor: 'pointer',
  color: colors.primary,
});

/**
 * Questionnaires that MUST be ordered by a doctor before they can be included
 * in the patient sendable notification.
 *
 * @requirement NEUR-2679
 */
const orderRequiredQuestionnaires = new Set<TTaskActionType>(['bai']);

const TaskListForm = ({
  templateSettingsByPlatform,
  inviteContactInfo,
  setInviteContactInfo,
  onChangeInviteContactInfo,
}: ITaskListProps) => {
  const { fm, editing, setEditingData } = useMyServiceContext<'_tasklist'>();

  const tasklist: Partial<ITaskList> = editing?.data ?? {};

  const locale = useAppSelector(s => s.settings.userSettings.uiLanguage) || browserLanguage;
  const platform = useSelector(s => s.session.platforms?.selected);
  const reduxDocs = useSelector(s => s.documents.sortedAndMergedDocuments ?? []);
  const orgSettings = useAppSelector(s => s.settings.orgSettings);
  const patientInformants = (useSelector(s => s.patient.informants) || []).filter(informant => !informant.removed);
  const patientMySQStatus = useSelector(s => s.myapp.mysqUserId);
  const sentInvites = useSelector(s => s.myapp.sentInvites);
  const patientContactInfo = useSelector(s => s.patient.contactInfo);
  const taskLists = useSelector(s => s.myapp.taskLists);
  const upcomingAppointments =
    useSelector(s => s.appointments).appointments?.filter(appo => {
      const appointmentTimeStamp = DateTime.fromMillis(partialDateToValue(appo.startDate))
        .plus({ hours: appo.startTime[0] })
        .plus({ minutes: appo.startTime[1] })
        .toMillis();
      return appointmentTimeStamp > Date.now();
    }) ?? [];

  const { allowedPatientCountries } = orgSettings;
  const templateSettings: TaskStimulusFormSettings = platform ? (templateSettingsByPlatform?.[platform] ?? {}) : {};

  const latestContraIndicationsToTreatmentDocument = getLatestContraIndicationsToTreatmentDoc(reduxDocs);

  const [selectedPlatform, setSelectedPlatform] = React.useState<Platform | undefined>(platform);
  const [addingNewInformant, setAddingNewInformant] = React.useState<boolean>(false);

  const showInformantFields = !!(tasklist.template_id && templateSettings[tasklist.template_id]?.taskKind === 'IROM');

  const tdcsAndRtmsDocs = getTDCSAndRTMSDocs(reduxDocs);
  const activeTdcsOrRtms = tdcsAndRtmsDocs.find(
    d => partialDateToValue(d.date) >= partialDateToValue(latestTreatmentPeriodDate) && !d.hasEnded,
  );

  // TODO Tää kai viel chokee? tai siis filtteröinti pvm perusteella + varmaan .map eikä .find?
  const orderedQuestionnaires =
    filterDocumentsByType(reduxDocs, ['doctorsOrdersAdditional']).find(d => d.additionalDocuments)
      ?.additionalDocuments ?? [];

  const taskActionTypeOptions = getTaskActionTypeOptions(platform, showInformantFields);
  const disabledOptions = taskActionTypeOptions.filter(o => {
    if (orderRequiredQuestionnaires.has(o) && !(orderedQuestionnaires as string[]).includes(o)) {
      return true;
    }
    if (o === 'contraIndicationsInquiry' && !activeTdcsOrRtms) return true;
    return false;
  });

  const platformSpecificTasklists = taskLists.filter(tl =>
    tl.tasks.every(t => platform && taskActionTypeOptions.includes(t)),
  );

  const currentRecurringPlatformSpecificTasklists = platformSpecificTasklists.filter(tl => {
    if (!tl.recurrence || !tl.start_inclusive) return false;
    const now = DateTime.now();
    const start = DateTime.fromFormat(tl.start_inclusive, TASK_DATE_FORMAT).setZone(tl.timezone);
    const end = tl.end_exclusive
      ? DateTime.fromFormat(tl.end_exclusive, TASK_DATE_FORMAT).setZone(tl.timezone).minus({ milliseconds: 1 })
      : undefined;
    if (!end) return now >= start;
    else return now >= start && now <= end;
  });

  const recurringDisabledOpts: TTaskActionType[] = currentRecurringPlatformSpecificTasklists
    .filter(tl => tl.id !== tasklist.id)
    .flatMap(tl => tl.tasks);

  const treatmentPeriodDocs = filterDocumentsByType(reduxDocs, ['ninmtTreatmentPeriod']);
  const latestTreatmentPeriod = treatmentPeriodDocs.sort(sortDocumentsDescending).at(0);
  const latestTreatmentPeriodDate = latestTreatmentPeriod?.date;

  /** function that merges given values to the existing tasklist */
  const onChangeTaskForm = (values: Partial<ITaskList>): void => {
    console.info(
      `set ${Object.entries(values)
        .map(([k, v]) => `${k}: ${JSON.stringify(v)}`)
        .join(', ')}`,
    );
    setEditingData?.({ ...tasklist, ...(values as ITaskList) });
  };

  const onChangeTemplateID = (values: TOnChangeValues): void => {
    const template_id = Object.values(values).at(0) as unknown;
    if (typeof template_id !== 'string' && template_id !== null && template_id !== undefined) {
      return;
      // log?
    }
    if (!template_id) {
      // TODO do we want to nullify the whole form here?
      onChangeTaskForm({ template_id: undefined, tasks: undefined, end_exclusive: undefined });
      return;
    }
    const template = templateSettings[template_id];
    if (!template) {
      return;
      // log? shouldnt happen tho
    }
    const isIROM = template.taskKind === 'IROM';
    const isRecurring = !!template.recurring;
    // what is happening here? TODO
    if (isIROM) setInviteContactInfo(undefined);
    else if (sentInvites.length === 0 && !firstInviteUpcoming && !patientMySQStatus) {
      setInviteContactInfo(patientContactInfo?.contacts ?? []);
    }
    //
    const { defaultSelectQuestionnaires, descriptionLocalized, recurringInterval } = template;
    const tasks = defaultSelectQuestionnaires
      .filter(isTaskActionType)
      .filter(t => !disabledOptions.includes(t) && !recurringDisabledOpts.includes(t));
    // ^^ i guess we want to filter disabled options here

    const every_nth = isRecurring ? mapRecurringIntervalToEveryNth(recurringInterval) : undefined;

    const recurrence: ITaskList['recurrence'] = isRecurring
      ? { recurring: true, every_nth, duration_cal_days: undefined, first_deadline: undefined }
      : { recurring: false, every_nth: undefined, duration_cal_days: undefined, first_deadline: undefined };

    const newDescription: string | undefined = descriptionLocalized[locale] ?? descriptionLocalized.fi;

    const start = parseSendDate({
      tasklist: { ...tasklist, template_id, tasks, description: newDescription, recurrence },
      templateSettings,
    });
    const start_inclusive = start ? (partialDateToStartInclusive(start) ?? undefined) : undefined;

    // TODO: Clear 'newInformant' at least when changing from IROM template to PROM template?

    onChangeTaskForm({ template_id, tasks, description: newDescription, recurrence, start_inclusive });
  };

  const onChangeInformantID = (values: TOnChangeValues): void => {
    const informant_id = Object.values(values).at(0) as unknown;
    if (typeof informant_id !== 'string' && informant_id !== undefined && informant_id !== null) {
      return;
      // log?
    }
    // what else do we need to do here??
    onChangeTaskForm({ informant_id });
  };

  const onChangeAppointmentID = (values: TOnChangeValues): void => {
    const appointment_id = Object.values(values).at(0) as unknown;
    if (typeof appointment_id !== 'string' && appointment_id !== null && appointment_id !== undefined) {
      return;
      // log?
    }
    if (!appointment_id) {
      // do we want to clear end_exclusive if appointment_id is unselected? TODO
      // ^^ can appointment_id even be unselected lol?
      onChangeTaskForm({ appointment_id: undefined, end_exclusive: undefined });
    } else {
      const selectedAppointment = upcomingAppointments.find(appointment => appointment.id === appointment_id);
      if (!selectedAppointment) return;

      const { startDate, startTime } = selectedAppointment;

      // should we set appointment's start or end as task's end time? TODO
      const end_exclusive = DateTime.fromObject({
        year: startDate[0],
        month: startDate[1] ?? undefined,
        day: startDate[2] ?? undefined,
        hour: startTime[0],
        minute: startTime[1],
      }).toFormat(TASK_DATE_FORMAT);

      onChangeTaskForm({ appointment_id, end_exclusive });
    }
  };

  // When selecting recurring, uncheck tasks which are already in some recurring stimulus
  // Reset recurring fields if recurring ticked off
  const onChangeIsRecurring = (values: TOnChangeValues): void => {
    const recurring = Object.values(values).at(0) as unknown;
    if (typeof recurring !== 'boolean' && recurring !== undefined && recurring !== null) return;
    const isRecurring = !!recurring;
    const { template_id } = tasklist;
    if (!template_id) return;
    if (isRecurring) {
      const tasks: ITaskList['tasks'] = tasklist.tasks?.filter(t => !recurringDisabledOpts.includes(t)) ?? [];
      // const {} = templateSettings;
      const recurrence: ITaskList['recurrence'] = {
        recurring: true,
        first_deadline: undefined,
        duration_cal_days: undefined,
        every_nth: undefined,
      };
      const end_exclusive = !tasklist.appointment_id ? undefined : tasklist.end_exclusive;
      onChangeTaskForm({
        recurrence,
        end_exclusive,
        tasks,
        // i guess we want to nullify possible informant_id and newInformant here??
        informant_id: undefined,
        newInformant: undefined,
      });
    } else {
      const recurrence: ITaskList['recurrence'] = {
        recurring: false,
        first_deadline: undefined,
        duration_cal_days: undefined,
        every_nth: undefined,
      };
      const end_exclusive = !tasklist.appointment_id ? undefined : tasklist.end_exclusive;
      onChangeTaskForm({ recurrence, end_exclusive });
    }
  };

  /** just update recurrence.every_nth value; simple */
  const onChangeEveryNth = (values: TOnChangeValues): void => {
    const every_nth = Object.values(values).at(0) as unknown;
    if (!isNumber(every_nth)) return;
    if (!tasklist.recurrence?.recurring) return;
    onChangeTaskForm({ recurrence: { ...tasklist.recurrence, every_nth } });
  };

  /**
   * just update recurrence.first_deadline. Also:
   * - set end_exclusive based on templateSettings (?)
   */
  const onChangeRecurringTaskFirstDeadline = (values: TOnChangeValues): void => {
    const deadline = Object.values(values).at(0) as unknown;
    if (!isPartialDate(deadline) && deadline !== undefined && deadline !== null) return; // log this? idk
    const first_deadline = deadline ? (partialDateToEndExclusive(deadline) ?? undefined) : undefined;
    const { recurrence } = tasklist;
    if (!recurrence?.recurring) return; // this shouldnt happen because this function can only be called when recurrence.recurring = true

    /** calculate end_exclusive from the changed "first deadline" */
    /** TODO Do we want to do this in every scenario? If end_exclusive already exists for example */
    const end_exclusive = parseRecurringTaskDeadline({
      tasklist: { ...tasklist, recurrence: { ...recurrence, first_deadline } },
      templateSettings,
    });

    onChangeTaskForm({ recurrence: { ...recurrence, first_deadline }, end_exclusive });
  };

  /**
   * just update end_exclusive, and
   * - if latest contraIndicationsInq is >30 days ago, auto-select it
   *
   * Do not update sendDate, it is parsed directly from data
   */
  const onChangeEndDate = (values: TOnChangeValues): void => {
    const endDate = Object.values(values).at(0) as unknown;
    if (!tasklist.template_id) return;
    if (!isPartialDate(endDate) && endDate !== undefined && endDate !== null) return; // log this? idk
    const end_exclusive = endDate ? partialDateToEndExclusive(endDate) : undefined;

    const start = parseSendDate({ tasklist: { ...tasklist, end_exclusive }, templateSettings });
    const start_inclusive = start ? (partialDateToStartInclusive(start) ?? undefined) : undefined;

    const dateDiff_contraIndicationToTreatment = calculateDaysDifference(
      latestContraIndicationsToTreatmentDocument?.date,
      endDate,
    );

    const newTasks = [...(tasklist.tasks ?? [])];

    // if latest contraIndicationsInq is >30 days ago, auto-select it
    if (dateDiff_contraIndicationToTreatment && Math.abs(dateDiff_contraIndicationToTreatment) > 30) {
      if (
        taskActionTypeOptions.includes('contraIndicationsInquiry') &&
        !tasklist.tasks?.includes('contraIndicationsInquiry') &&
        !disabledOptions.includes('contraIndicationsInquiry') &&
        !recurringDisabledOpts.includes('contraIndicationsInquiry')
      ) {
        newTasks.push('contraIndicationsInquiry');
      }
    }
    onChangeTaskForm({ end_exclusive, tasks: newTasks, start_inclusive });
  };

  /**
   * just update tasks; simple
   *
   * NOTE: previously `sendDate` was updated by the hook related to this tasks field,
   * but now as sendDate is parsed directly from data, it (`ninmtPreInquiry`) is taken
   * into action in the `parseSendDate`-function
   */
  const onChangeTaskActions = (values: TOnChangeValues): void => {
    const actions = Object.values(values).at(0) as unknown;
    if (!Array.isArray(actions) && actions !== null && actions !== undefined) {
      return;
      // log?
    }
    const tasks = actions?.filter(isTaskActionType) ?? [];
    const start = parseSendDate({ tasklist: { ...tasklist, tasks }, templateSettings });
    const start_inclusive = start ? (partialDateToStartInclusive(start) ?? undefined) : undefined;
    onChangeTaskForm({ tasks, start_inclusive });
  };

  /** just update description; simple */
  const onChangeDescription = (values: TOnChangeValues): void => {
    const description = Object.values(values).at(0) as unknown;
    if (typeof description !== 'string' && description !== null && description !== undefined) {
      return;
      // log?
    }
    onChangeTaskForm({ description: description ?? '' });
  };

  // Empty title, tasks and description fields if platform is changed
  React.useEffect(() => {
    if (platform !== selectedPlatform) {
      // can we get rid of this?? TODO
      onChangeTaskForm({ template_id: undefined, tasks: undefined, description: undefined });
      setSelectedPlatform(platform);
    }
  }, [platform]);

  // what is this? TODO
  const firstInviteUpcoming =
    sentInvites.length === 0 &&
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    taskLists.filter(tl => tl.task_kind === 'PROM' || (!tl.task_kind && !tl.informant_id)).length > 0;

  const visibleTemplateIDsByKind = (taskKind: 'PROM' | 'IROM') =>
    Object.keys(templateSettings ?? {}).filter(
      key => !templateSettings?.[key]?.hidden && templateSettings?.[key]?.taskKind === taskKind,
    );

  const template_id_options: ISelectByCategory['options'] = [
    { title: fm('myService.forPatient'), values: visibleTemplateIDsByKind('PROM') },
    { title: fm('myService.forRelative'), values: visibleTemplateIDsByKind('IROM') },
  ].filter(category => category.values.length > 0);

  const defaultOrgSSNLocale: PatientCountryCode =
    allowedPatientCountries && allowedPatientCountries.length > 1
      ? (allowedPatientCountries.find(apc => apc.isDefault)?.code ?? 'FIN')
      : 'FIN';

  return (
    <>
      <FormRow title="myService.taskListTitle">
        <InputHandler
          name="template_id"
          type="SelectByCategory"
          editing={!!editing}
          options={template_id_options}
          optionFormatter={option => formateTemplateIDOption({ option, templateSettings, locale, fm })}
          formData={{
            document: { template_id: tasklist.template_id },
            onChange: onChangeTemplateID,
          }}
          placeholder="myService.taskListTitlePlaceholder"
        />
        {!tasklist?.template_id ? (
          <div style={{ marginTop: '2rem', fontWeight: 600 }}>{fm('myService.firstChooseTitle')}</div>
        ) : undefined}
      </FormRow>

      {/** NOTICE: Only show the title field if not selected */}
      {tasklist?.template_id && templateSettings?.[tasklist.template_id]?.titleDescLocalized?.[locale] && (
        <FormRow>
          {templateSettings?.[tasklist.template_id]?.titleDescLocalized?.[locale] ??
            templateSettings?.[tasklist.template_id]?.titleDescLocalized?.['fi']}
          {/** Displays for example "Muu vapaamuotoinen heräte" if having selected "other" template_id */}
        </FormRow>
      )}
      <FormRow
        title={'myService.patientNotMysq'}
        // TODO?
        condition={
          !!(
            !patientMySQStatus &&
            sentInvites.length === 0 &&
            editing?.type === '_tasklist' &&
            !firstInviteUpcoming &&
            tasklist?.template_id &&
            !showInformantFields
          )
        }
        description={fm('myService.patientNotMysqInfo')}
      >
        <div>
          <div style={{ marginBottom: '0.5rem' }}>{fm('profile.phoneNumbers')}</div>
          <Container>
            <Item xs={5}>
              <InputHandler
                type="SingleValueList"
                name="smsList"
                editing={!!editing}
                value={inviteContactInfo?.filter(ci => ci.type === 'sms').map(sms => sms.value)}
                sortMethod={(a, b) => {
                  const aRank = inviteContactInfo?.find(ci => ci.value === a)?.rank;
                  const bRank = inviteContactInfo?.find(ci => ci.value === b)?.rank;
                  return (
                    (isNumber(aRank) ? aRank : Number.MAX_SAFE_INTEGER) -
                    (isNumber(bRank) ? bRank : Number.MAX_SAFE_INTEGER)
                  );
                }}
                formData={{
                  onChange: onChangeInviteContactInfo,
                  document: { smsList: inviteContactInfo?.filter(ci => ci.type === 'sms').map(sms => sms.value) },
                }}
                disableSaveButton={value => {
                  if (typeof value === 'string')
                    return (
                      value.length === 0 ||
                      !phone(value, {
                        country: defaultOrgSSNLocale,
                        // ^^ what the hell is this? TODO
                      }).isValid
                    );
                  return false;
                }}
                disabledTooltip={fm('profile.notValidSms')}
                component={props => {
                  return <TextField {...props} type="TextField" placeholder={undefined} />;
                }}
              />
            </Item>
          </Container>
          <div style={{ marginBottom: '0.5rem', marginTop: '2rem' }}>{fm('profile.emails')}</div>
          <Container>
            <Item xs={5}>
              <InputHandler
                type="SingleValueList"
                name="emailList"
                editing={!!editing}
                value={inviteContactInfo?.filter(ci => ci.type === 'email').map(email => email.value)}
                sortMethod={(a, b) => {
                  const aRank = inviteContactInfo?.find(ci => ci.value === a)?.rank;
                  const bRank = inviteContactInfo?.find(ci => ci.value === b)?.rank;
                  return (
                    (isNumber(aRank) ? aRank : Number.MAX_SAFE_INTEGER) -
                    (isNumber(bRank) ? bRank : Number.MAX_SAFE_INTEGER)
                  );
                }}
                formData={{
                  onChange: onChangeInviteContactInfo,
                  document: {
                    emailList: inviteContactInfo?.filter(ci => ci.type === 'email').map(email => email.value),
                  },
                }}
                disableSaveButton={value => {
                  if (typeof value === 'string') return value.length === 0 || !emailValidate(value);
                  return false;
                }}
                disabledTooltip={fm('profile.notValidEmail')}
                component={props => {
                  return <TextField {...props} type="TextField" placeholder={undefined} />;
                }}
              />
            </Item>
          </Container>
        </div>
      </FormRow>
      <FormRow title="delegates.informants" condition={showInformantFields}>
        <div style={{ maxWidth: '70%' }}>
          <InputHandler
            name="informantId"
            type="Select"
            options={patientInformants.map(informant => informant.id)}
            editing={!!editing}
            formData={{
              document: { informantId: tasklist.informant_id },
              onChange: onChangeInformantID,
            }}
            optionFormatter={o => {
              // this fails? because the option is an id? TODO
              if (typeof o === 'string' && !!parsePatientDemographics(defaultOrgSSNLocale, { ssn: o })) {
                const person = tasklist.newInformant;
                return <Censorable>{`${person?.lastName} ${person?.firstName}`}</Censorable>;
              } else {
                const person = patientInformants.find(r => r.id === o);
                return <Censorable>{`${person?.lastNames} ${person?.firstNames}`}</Censorable>;
              }
            }}
            placeholder={'delegates.informants'}
          />
          {editing && !addingNewInformant ? (
            <div style={{ marginTop: '1rem' }}>
              <StyledButton onClick={() => setAddingNewInformant(true)}>{`+ ${fm('general.new')}`}</StyledButton>
            </div>
          ) : null}
          {editing && addingNewInformant ? (
            <div
              style={{
                marginTop: '1rem',
                backgroundColor: colors.lightGray,
                padding: '1rem',
                borderRadius: '1rem',
              }}
            >
              <NewInformant
                fm={fm}
                locale={locale}
                editing={!!editing}
                setAddingNewInformant={setAddingNewInformant}
                onChangeTaskForm={onChangeTaskForm}
              />
            </div>
          ) : null}
        </div>
      </FormRow>
      <FormRow
        title="myService.linkedAppointment"
        condition={
          !!(
            tasklist?.template_id &&
            !tasklist?.recurrence?.recurring &&
            (upcomingAppointments.length > 0 || tasklist?.appointment_id) &&
            (!tasklist?.id || (tasklist?.id && tasklist?.appointment_id)) // If the taskList has been saved already without being linked, dont allow linking
          )
        }
      >
        <InputHandler
          name="appointmentId"
          type="Select"
          editing={!!editing}
          formData={{
            document: { appointmentId: tasklist.appointment_id },
            onChange: onChangeAppointmentID,
          }}
          options={upcomingAppointments.map(appointment => appointment.id)}
          optionFormatter={o => {
            const appointment = upcomingAppointments.find(appo => appo.id === o);
            if (!appointment) return `${o}`; // as a backup, return just the option
            let formatted = '';
            if (appointment.startDate) formatted += formatPartialDate(appointment.startDate);
            if (appointment.startTime) formatted += ` ${formatTime(appointment.startTime)}`;
            if (appointment.externalAppointmentType) {
              if (formatted.length > 0) formatted += ', ';
              formatted += appointment.externalAppointmentType;
            }
            return formatted;
          }}
          placeholder={'myService.linkedAppointment'}
        />
      </FormRow>
      <FormRow title="myService.recurring" condition={shouldAllowRecurringTasks({ platform, tasklist })}>
        <InputHandler
          name="recurring"
          type="CheckboxSingle"
          option="true"
          showLabel={false}
          editing={!!editing}
          disabled={!!(tasklist.id && tasklist.recurrence?.recurring)} // Disabled changing recurring if the taskList has already been saved as recurring
          formData={{
            document: { recurring: tasklist.recurrence?.recurring },
            onChange: onChangeIsRecurring,
          }}
        />
      </FormRow>
      <FormRow title="myService.recurringInterval" condition={!!tasklist.recurrence?.recurring}>
        <InputHandler
          name="every_nth"
          type="Select"
          editing={!!editing}
          options={[1, 2, 4]} // should this come from somewhere else?
          placeholder={'myService.recurringInterval'}
          optionFormatter={o => fm(`myService.opts.every_nth.${o}`)}
          formData={{
            document: { every_nth: tasklist.recurrence?.every_nth },
            onChange: onChangeEveryNth,
          }}
        />
      </FormRow>
      <FormRow title="myService.recurringTaskDeadline" condition={!!tasklist.recurrence?.recurring}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div>
            <InputHandler
              name="recurringTaskDeadline"
              type="PartialDate"
              editing={
                !!(
                  editing &&
                  (!tasklist.id ||
                    // if tasklist has been saved but the saved first_deadline is still in the future, allow editing
                    (tasklist.id &&
                      tasklist.recurrence?.first_deadline &&
                      DateTime.fromFormat(tasklist.recurrence?.first_deadline, 'yyyy-MM-dd HH:mm:ss') >=
                        DateTime.now()))
                )
              }
              formData={{
                document: {
                  recurringTaskDeadline: tasklist.recurrence?.first_deadline
                    ? partialDateFromEndExclusive(tasklist.recurrence?.first_deadline)
                    : undefined,
                },
                onChange: onChangeRecurringTaskFirstDeadline,
              }}
              dateHook={{
                dateHookFloor: nowPartialDate(),
                dateHookCeiling: tasklist.end_exclusive
                  ? partialDateFromEndExclusive(tasklist.end_exclusive)
                  : undefined,
              }}
            />
          </div>
          {tasklist.recurrence?.first_deadline ? (
            <span style={{ paddingBottom: 5 }}>
              {fm(
                `general.jsDate.${DateTime.fromFormat(tasklist.recurrence.first_deadline, 'yyyy-MM-dd HH:mm:ss').minus({ milliseconds: 1 }).get('weekday') % 7}`,
              )}
            </span>
          ) : null}
        </div>
      </FormRow>
      <FormRow title={'myService.deadline'} condition={!tasklist.recurrence?.recurring && !!tasklist?.template_id}>
        <>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div>
              <InputHandler
                name="deadline"
                type="PartialDate"
                editing={!!(editing && !tasklist.appointment_id)}
                formData={{
                  document: {
                    deadline: tasklist.end_exclusive ? partialDateFromEndExclusive(tasklist.end_exclusive) : undefined,
                  },
                  onChange: onChangeEndDate,
                }}
                dateHook={{ dateHookFloor: nowPartialDate() }}
                isNotCancellable
              />
            </div>
            {tasklist.appointment_id &&
            upcomingAppointments.find(appointment => appointment.id === tasklist.appointment_id)?.startTime ? (
              <span style={{ marginLeft: '0.5rem', fontWeight: 600 }}>
                {/** TODO: This should prob be just formatted using the end_exclusive in all scenarios */}
                {formatTime(
                  upcomingAppointments.find(appointment => appointment.id === tasklist.appointment_id)?.startTime,
                )}
              </span>
            ) : null}
            {tasklist.end_exclusive ? (
              <span style={tasklist.appointment_id ? { marginLeft: '1rem' } : { paddingBottom: 5 }}>
                {fm(
                  `general.jsDate.${DateTime.fromFormat(tasklist.end_exclusive, TASK_DATE_FORMAT).minus({ milliseconds: 1 }).get('weekday') % 7}`,
                )}
              </span>
            ) : null}
          </div>
        </>
      </FormRow>
      <FormRow
        title={'myService.taskDuration'}
        condition={!!(tasklist?.template_id && !tasklist.recurrence?.recurring)}
      >
        <div>
          {(() => {
            const { start_inclusive, end_exclusive, timezone } = tasklist;
            if (!start_inclusive || !end_exclusive) return null;
            const start = partialDateFromStartInclusive(start_inclusive);
            const end = partialDateFromEndExclusive(end_exclusive);
            if (!start || !end) return null;
            return (
              <StyledDateRange
                from={start}
                to={end}
                controls={{
                  from: (
                    <LeftRightControls
                      action={{
                        left: () => {
                          const _current = DateTime.fromFormat(start_inclusive, TASK_DATE_FORMAT);
                          const _new = _current.minus({ days: 1 });
                          onChangeTaskForm({ start_inclusive: _new.toFormat(TASK_DATE_FORMAT) });
                        },
                        right: () => {
                          const _current = DateTime.fromFormat(start_inclusive, TASK_DATE_FORMAT);
                          const _new = _current.plus({ days: 1 });
                          onChangeTaskForm({ start_inclusive: _new.toFormat(TASK_DATE_FORMAT) });
                        },
                      }}
                      disabled={{
                        left:
                          DateTime.fromFormat(start_inclusive, TASK_DATE_FORMAT).setZone(timezone) <= DateTime.now(),
                        right:
                          DateTime.fromFormat(start_inclusive, TASK_DATE_FORMAT).setZone(timezone).plus({ days: 1 }) >=
                          DateTime.fromFormat(end_exclusive, TASK_DATE_FORMAT).setZone(timezone),
                      }}
                    />
                  ),
                }}
              />
            );
          })()}
        </div>
      </FormRow>
      {/** Original sendDate InputHandler, replaced with above custom component. This is only used when recurring is checked. */}
      <FormRow title="myService.sendDate" condition={!!(tasklist?.template_id && tasklist.recurrence?.recurring)}>
        {(() => {
          const sendDate = parseSendDate({ tasklist, templateSettings });
          return (
            <>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div>
                  <InputHandler
                    name="sendDate"
                    type="PartialDate"
                    editing={false} // shown only for recurring tasks, always disabled
                    formData={{
                      document: { sendDate },
                      onChange: onChangeTaskForm,
                    }}
                    // no dateHook needed because editing=false
                    isNotCancellable
                  />
                </div>
                {sendDate ? (
                  <span style={{ paddingBottom: 5 }}>
                    {fm(
                      `general.jsDate.${new Date(sendDate[0], sendDate[1] ? sendDate[1] - 1 : 0, sendDate[2] ?? 1).getDay()}`,
                    )}
                  </span>
                ) : null}
              </div>
              {/** "Lähetyspäivä lasketaan automaattisesti ensimmäisestä määräpäivästä" */}
              {sendDate ? <div>{fm('myService.sendDateRecurringInfo')}</div> : null}
            </>
          );
        })()}
      </FormRow>
      {/** Deadline row second time as fields wanted in different order depending on if recurred checked */}
      <FormRow title={'myService.lastRecurringTaskDeadline'} condition={!!tasklist.recurrence?.recurring}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div>
            <InputHandler
              name="end_exclusive"
              type="PartialDate"
              editing={!!editing}
              formData={{
                document: {
                  end_exclusive: tasklist.end_exclusive
                    ? partialDateFromEndExclusive(tasklist.end_exclusive)
                    : undefined,
                },
                onChange: onChangeEndDate,
              }}
              dateHook={{
                dateHookFloor: nowPartialDate(),
                dateHookCeiling: undefined, // nothing here? possible appoint does not affect this
              }}
              isNotCancellable
            />
          </div>
          {tasklist.end_exclusive ? (
            <span style={{ paddingBottom: 5 }}>
              {fm(
                `general.jsDate.${DateTime.fromFormat(tasklist.end_exclusive, TASK_DATE_FORMAT).minus({ milliseconds: 1 }).get('weekday') % 7}`,
              )}
            </span>
          ) : null}
        </div>
      </FormRow>
      <FormRow title="myService.tasksForTaskList" condition={!!tasklist?.template_id}>
        <InputHandler
          name="tasks"
          type="Checkbox"
          editing={!!editing}
          options={taskActionTypeOptions}
          optionFormatter={option =>
            formatTaskActionType({
              fm,
              option,
              recurringDisabledOpts: recurringDisabledOpts as string[],
              latestContraIndicationDocument: latestContraIndicationsToTreatmentDocument,
              tasklist,
              platform,
              locale,
              informantVersion: showInformantFields,
            })
          }
          formData={{ document: { tasks: tasklist.tasks }, onChange: onChangeTaskActions }}
          disabledOptions={
            tasklist.recurrence?.recurring
              ? [...(disabledOptions ?? []), ...(recurringDisabledOpts as string[])]
              : disabledOptions
          }
        />
        {tasklist.recurrence?.recurring && recurringDisabledOpts.length > 0 ? (
          <div style={{ color: colors.tertiaryText }}>{fm('myService.alreadyRecurring')}</div>
        ) : null}
      </FormRow>
      <FormRow title="myService.taskListInfo" condition={!!tasklist?.template_id}>
        <StyledTextArea
          name="description"
          value={tasklist.description ?? ''}
          onChange={e => onChangeDescription({ description: e.currentTarget.value })}
          placeholder={fm('myService.taskListInfoPlaceholder')}
          rows={14}
        />
      </FormRow>
    </>
  );
};

export type TemplateSettingsByPlatform = {
  [platform in Platform]?: TaskStimulusFormSettings;
};

interface ITaskListProps {
  inviteContactInfo: (INeuroContactInfo | INeuroUIContactInfo)[] | undefined;
  setInviteContactInfo: React.Dispatch<React.SetStateAction<(INeuroContactInfo | INeuroUIContactInfo)[] | undefined>>;
  onChangeInviteContactInfo: (values: TOnChangeValues) => void;
  templateSettingsByPlatform: TemplateSettingsByPlatform;
}

export default TaskListForm;
